import { useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  type BounceKind,
  type SwitchPort,
  useBouncePort,
  useRefreshSwitchPorts,
  useSwitchPorts,
} from '../../../features/locations/use-switch-ports'
import { useIdOrCode } from '../../use-id-or-code'
import { Btn, ConfirmDialog, Empty, Inline, SubCrumbs, td, th } from './controls'
import { Bar, Dot, mono, Note, Panel, SkelPanels } from './ui'

const portTone = (s: SwitchPort['status']) => (s === 'connected' ? 'ok' : s === 'disabled' ? 'bad' : 'off')

interface Pending {
  port: SwitchPort
  kind: BounceKind
  prompt: string
}

export function SignalSwitchPorts() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const ports = useSwitchPorts(idOrCode)
  const bounce = useBouncePort()
  const refreshPorts = useRefreshSwitchPorts()

  const [confirm, setConfirm] = useState<Pending | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const crumbs = <SubCrumbs idOrCode={idOrCode} section="network" page="switch-ports" />

  if (location.isLoading || ports.isLoading)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Switch ports" />
        <div className="overflow-auto">
          <SkelPanels n={1} />
        </div>
      </div>
    )
  const storeCode = location.data?.aptosStoreCode ?? location.data?.code ?? null
  if (location.error || ports.error || !ports.data || !storeCode)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Switch ports" />
        <Note error>
          Failed to load switch ports: {ports.error ? apiErrorMessage(ports.error) : 'Store code not found.'}
        </Note>
      </div>
    )

  const list = ports.data.ports
  const up = list.filter((p) => p.status === 'connected').length

  const start = (port: SwitchPort, kind: BounceKind) => {
    setNotice(null)
    bounce.mutate(
      { storeCode, portId: port.portId, kind },
      {
        onSuccess: (r) => {
          if (r.status === 'needs-confirmation') setConfirm({ port, kind, prompt: r.prompt })
          else setNotice(`${kind === 'poe' ? 'PoE' : 'Port'} ${port.portId} bounced`)
        },
      },
    )
  }

  const confirmed = () => {
    if (!confirm) return
    const { port, kind } = confirm
    bounce.mutate(
      { storeCode, portId: port.portId, kind, confirmed: true },
      {
        onSuccess: () => {
          setNotice(`${kind === 'poe' ? 'PoE' : 'Port'} ${port.portId} bounced`)
          setConfirm(null)
        },
      },
    )
  }

  const busy = bounce.isPending

  return (
    <div className="flex h-full flex-col">
      <Bar
        crumbs={crumbs}
        title="Switch ports"
        meta={
          <span className="text-[12px] text-[var(--color-muted)]" style={mono}>
            {up}/{list.length} up{ports.data.switchVendor ? ` · ${ports.data.switchVendor}` : ''}
          </span>
        }
        right={
          <Btn
            disabled={refreshing}
            onClick={async () => {
              setRefreshing(true)
              try {
                await refreshPorts()
              } finally {
                setRefreshing(false)
              }
            }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Btn>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <Panel title="Ports">
          {(notice || (bounce.error && !confirm)) && (
            <div className="border-b border-[var(--color-border)] px-3 py-1.5">
              {bounce.error && !confirm ? (
                <Inline tone="bad">{apiErrorMessage(bounce.error)}</Inline>
              ) : (
                <Inline tone="ok">{notice}</Inline>
              )}
            </div>
          )}
          {list.length === 0 ? (
            <Empty>No switch ports reported for this store.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] border-collapse" aria-label="Switch ports">
                <thead>
                  <tr>
                    {['Port', 'Status', 'VLAN', 'Client', 'In / Out', ''].map((h) => (
                      <th key={h || 'actions'} scope="col" className={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.map((p) => (
                    <tr key={p.portId} className="hover:bg-[var(--color-surface-2)]">
                      <td className={td}>
                        <span style={mono}>{p.portId}</span>
                        {p.name && <span className="ml-2 text-[var(--color-muted)]">{p.name}</span>}
                      </td>
                      <td className={td}>
                        <span className="inline-flex items-center gap-1.5">
                          <Dot tone={portTone(p.status)} />
                          {p.status}
                        </span>
                      </td>
                      <td className={td} style={mono}>
                        {p.vlanTag ?? '-'}
                      </td>
                      <td className={td}>
                        {p.clientMac ? (
                          <span>
                            {p.clientDescription ?? p.clientIp ?? ''}
                            <span className="ml-2 text-[var(--color-muted)]" style={mono}>
                              {p.clientMac}
                            </span>
                          </span>
                        ) : (
                          <span className="text-[var(--color-muted)]">-</span>
                        )}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`} style={mono}>
                        {p.trafficReceived || p.trafficSent
                          ? `${p.trafficReceived ?? '-'} / ${p.trafficSent ?? '-'}`
                          : '-'}
                      </td>
                      <td className={`${td} text-right`}>
                        <span className="inline-flex gap-1">
                          <Btn disabled={busy || !p.enabled} onClick={() => start(p, 'port')}>
                            Bounce
                          </Btn>
                          <Btn disabled={busy || !p.enabled} onClick={() => start(p, 'poe')}>
                            PoE
                          </Btn>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <ConfirmDialog
        open={confirm !== null}
        title={`Bounce ${confirm?.kind === 'poe' ? 'PoE on ' : ''}port ${confirm?.port.portId ?? ''}?`}
        body={confirm?.prompt}
        confirmLabel="Yes, bounce"
        danger
        pending={busy}
        error={bounce.error ? apiErrorMessage(bounce.error) : null}
        onConfirm={confirmed}
        onClose={() => {
          bounce.reset()
          setConfirm(null)
        }}
      />
    </div>
  )
}
